import { createContext, useContext, useEffect, useState } from "react";
import request from "../config/request";
import {
    GoogleAuthProvider,
    signInWithPopup,
    GithubAuthProvider
} from "firebase/auth";
import { auth } from "../config/firebase-config";

const UserAuthContext = createContext();

export function UserAuthContextProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isNewUser, setIsNewUser] = useState(false);
    const [token, setToken] = useState(localStorage.getItem("token") || null);
    const [bookingId, setBookingId] = useState(null);
    const [bookingDetails, setBookingDetails] = useState(null);

    // function to get the token from localStorage and make the fetchUserProfile function call
    function getTokenAndFetchUserProfile() {
        const token = localStorage.getItem("token");
        if (token) {
            setToken(token);
            fetchUserProfile(token);
        }
    }

    // Helper to set token in localStorage and state
    function saveToken(token) {
        localStorage.setItem("token", token);
        setToken(token);
    }

    // Login with email/password
    async function logIn(email, password) {
        try {
            const res = await request.post("/api/user/auth/email/login", { email, password });
            if (res.data.token) {
                saveToken(res.data.token);
                await fetchUserProfile(res.data.token);
                return res.data;
            } else {
                throw new Error(res.data.message || "Login failed");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Register new user
    async function signUp(email, password, name) {
        try {
            const res = await request.post("/api/user/auth/email/register", { name, email, password });
            if (res.data.response) {
                return res.data;
            } else {
                throw new Error(res.data.message || "Registration failed");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Social login (Google/Github) with Firebase ID token
    async function socialSignIn(firebaseIdToken) {
        try {
            const res = await request.post("/api/user/auth/social/auth", { token: firebaseIdToken });
            if (res.data.token) {
                saveToken(res.data.token);
                setIsNewUser(!!res.data.isNewUser);
                await fetchUserProfile(res.data.token);
                return res.data;
            } else {
                throw new Error(res.data.message || "Social login failed");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Get current user profile
    async function fetchUserProfile(jwt = token) {
        if (!jwt) return;
        try {
            const res = await request.get("/api/user/auth/protected/me");
            console.log(res);
            setUser(res.data);
        } catch (error) {
            setUser(null);
        }
    }

    //Get booking details
    async function fetchBookingDetails(bookingId) {
        if (!bookingId) return;
        let response;
        try {
            response = await request.get(`/api/user/auth/protected/details/${bookingId}`);
            setBookingDetails(response.data.data);
        } catch (error) {
            setBookingDetails(null);
        }
        return response.data.data;
    }

    // Password reset: Send password reset email via API
    async function forgetpassword(email) {
        try {
            const res = await request.post("/api/user/auth/forget-password", { email });
            if (res.data.response) {
                return res.data;
            } else {
                throw new Error(res.data.message || "Failed to send password reset email");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Logout
    function logOut() {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
        setIsNewUser(false);
        window.$crisp?.push?.(["do", "session:reset"]);
        return Promise.resolve();
    }

    // Google sign-in: get Firebase ID token and pass to socialSignIn
    async function googleSignIn() {
        const provider = new GoogleAuthProvider();
        provider.addScope('profile');
        provider.addScope('email');
        provider.setCustomParameters({ prompt: 'select_account' });
        const result = await signInWithPopup(auth, provider);
        console.log(result);
        const firebaseIdToken = await result.user.getIdToken();
        return socialSignIn(firebaseIdToken);
    }

    // Github sign-in: get Firebase ID token and pass to socialSignIn
    async function githubSignIn() {
        const provider = new GithubAuthProvider();
        provider.addScope('user');
        provider.addScope('user:email');
        provider.setCustomParameters({ allow_signup: 'true' });
        const result = await signInWithPopup(auth, provider);
        console.log(result);
        const firebaseIdToken = await result.user.getIdToken();
        return socialSignIn(firebaseIdToken);
    }

    const values = {
        user,
        setUser,
        isNewUser,
        setIsNewUser,
        logIn,
        signUp,
        logOut,
        forgetpassword,
        googleSignIn,
        githubSignIn,
        token,
        setToken,
        getTokenAndFetchUserProfile,
        saveToken,
        bookingId,
        setBookingId,
        bookingDetails,
        setBookingDetails,
        fetchBookingDetails
    };

    return (
        <UserAuthContext.Provider value={values}>
            {children}
        </UserAuthContext.Provider>
    );
}

export function useUserAuth() {
    return useContext(UserAuthContext);
}

export default UserAuthContextProvider;
